<script lang="ts" setup>
import { ref, computed, onBeforeMount, onMounted } from 'vue';
import { DoughnutChart, useDoughnutChart } from 'vue-chart-3';
import { Chart, ChartData, ChartOptions, registerables } from 'chart.js';
import { WidgetModel, WidgetConfigModel } from '@/modules/core/dashboards/services/DashboardsService';
import AiService from '@/modules/core/common/services/AiService';
import { useAlerts } from '@/plugins/alerts';
import { useLocalization } from '@/plugins/localization';
import WidgetComponentWrapper from "@/modules/core/dashboards/components/helpers/WidgetComponentWrapper.vue";
import FieldText from '@/modules/core/dashboards/components/properties/FieldText.vue';
import FieldDimensions from '@/modules/core/dashboards/components/properties/FieldDimensions.vue';

Chart.register(...registerables);

const { $alert } = useAlerts();
const { $t } = useLocalization();

defineOptions({
    name: "AiUsageWidget",
    components: {
        DoughnutChart
    }
});

interface AiUsage {
    limitInDollars: number;
    remainingLimitInDollars: number;
    sumPriceInDollars: number;
}

interface ChartVisibility {
    header: boolean;
    legend: boolean;
}

const config = defineModel<WidgetConfigModel>('config');

const props = defineProps<{
    widget: WidgetModel;
    propertiesMode: boolean;
}>();

const loaded = ref(false);
const data = ref<AiUsage>();

const chartOptions = computed<ChartOptions<"doughnut">>(() => ({
    responsive: true,
    maintainAspectRatio: false,
    scales: {},
    plugins: {
        legend: {
            display: chartVisibility.value.legend,
            position: 'bottom',
            fullSize: true,
            title: {
                display: true,
            }
        },
        tooltip: {
            callbacks: {
                label: function(context)
                {
                    return `${context.label}: $${context.parsed.toFixed(2)}`;
                }
            }
        },
        title: {
            display: chartVisibility.value.header,
            text: $t('[[[Dostępny limit]]]'),
        },
        subtitle: {
            display: chartVisibility.value.header,
            text: `$${data.value?.remainingLimitInDollars.toFixed(2)}$ / $${data.value?.limitInDollars}`,
            padding: {
                bottom: 16
            }
        }
    },
}));

const chartColors = ref<string[]>([]);
const chartVisibility = ref<ChartVisibility>({
    header: true,
    legend: true
});

const chartData = computed<ChartData<"doughnut">>(() => ({
    labels: [$t('[[[Dostępne]]]'), $t('[[[Wykorzystane]]]')],
    datasets: [
        {
            data: [data.value?.remainingLimitInDollars, data.value?.sumPriceInDollars],
            backgroundColor: chartColors.value
        },
    ],
}));

const { doughnutChartProps } = useDoughnutChart({
    chartData: chartData,
    options: chartOptions,
});

const loadData = async (): Promise<void> =>
{
    try
    {
        loaded.value = false;
        data.value = await AiService.getOpenAiUsage();
    }
    catch (ex: any)
    {
        $alert.error(ex.message || $t('[[[Nie udało się załadować widgetu AI.]]]'));
    }
    finally
    {
        loaded.value = true;
    }
};

const initChartOptions = (form?: any) =>
{
    const height = parseInt(form ? form.h : config.value.height);
    const width = parseInt(form ? form.w : config.value.width);

    if (width === 2)
    {
        chartVisibility.value = {
            header: false,
            legend: false
        };
    }

    if (height === 2)
    {
        chartVisibility.value.header = false;
    }
};

onBeforeMount(async (): Promise<void> =>
{
    initChartOptions();
    await loadData();
});

onMounted(() =>
{
    const style = getComputedStyle(document.body);

    chartColors.value = [
        style.getPropertyValue('--bs-primary'),
        style.getPropertyValue('--bs-gray-300')
    ];
});
</script>

<template>
    <widget-component-wrapper :properties-mode="propertiesMode">
        <template #default>
            <div class="ai-usage" v-if="loaded">
                <doughnut-chart v-bind="doughnutChartProps" />
            </div>
        </template>
        <template #properties>
            <field-text :model-value="widget.name" :label="$t('[[[Nazwa]]]')" disabled />
            <field-text :model-value="widget.description" :label="$t('[[[Opis]]]')" textarea disabled />
            <field-dimensions v-model:width="config.width" v-model:height="config.height" />
        </template>
    </widget-component-wrapper>
</template>

<style lang="scss" scoped>
.ai-usage {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    & > :deep(div) {
        height: 100%;
        flex: 1;
    }
}
</style>
